// disabling eslint temporarily for legacy files until they can be refactored
/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import Select, { components } from "react-select";
import { Col, Button, Form, Row, Spinner, ButtonGroup } from "react-bootstrap";
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
} from "react-bootstrap-table2-paginator";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";
import filterFactory from "react-bootstrap-table2-filter";
import clsx from "clsx";
import { GeneralError } from "../../Errors/GeneralError";
import { multiSelectStyle } from "../../../styles/styles";
import ScrollMenu from "../../Scrolls/ScrollMenu";
import { sortingCarets } from "../utils";
import {
  Portlet,
  PortletBody,
} from "../../../../components/GenericPortlet/Portlet";
import language from "../../../utils/en.json";
import { ALERT_TYPES } from "../../../utils/alertTypes";
import CountryMappings from "../../../utils/CountryCoordinatesMapping";

const formatLocationName = (location) => {
  if (!location || typeof location !== "string") {
    return "";
  }

  if (location.length === 2) {
    // country code
    return CountryMappings[location].name;
  }

  return location.replaceAll("null, ", "");
};

export function RiskDetectionTable(props) {
  const {
    isError,
    isLoading: isParentLoading,
    intl,
    tableData,
    onPageChange,
    onSizePerPageChange,
    sizePerPageList,
    keyField,
    showPagination,
    componentIsForPdf,
  } = props;

  const [isLoading, setLoading] = useState(true);
  const [filteredTableData, setFilteredTableData] = useState([]);

  const [showRiskDetectionMenu, setShowRiskDetectionMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLocationMenu, setShowLocationMenu] = useState(false);
  const [showSeverityMenu, setShowSeverityMenu] = useState(false);

  const riskDetectionRef = useRef();
  const userRef = useRef();
  const locationRef = useRef();
  const severityRef = useRef();

  const [riskDetectionOptions, setRiskDetectionOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [severityOptions, setSeverityOptions] = useState([]);

  const [riskDetectionSelectedOptions, setRiskDetectionSelectedOptions] =
    useState([]);
  const [userSelectedOptions, setUserSelectedOptions] = useState([]);
  const [locationSelectedOptions, setLocationSelectedOptions] = useState([]);
  const [severitySelectedOptions, setSeveritySelectedOptions] = useState([]);

  const [numPerPage, setNumPerPage] = useState(10);
  const [tableSort, setTableSort] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);

  // Handles setting the data list to be the correct filtered list
  useEffect(() => {
    if (tableData?.length) {
      const updatedFilteredData = filterTable(tableData);
      setFilteredTableData(updatedFilteredData);
    }
  }, [
    riskDetectionSelectedOptions,
    userSelectedOptions,
    locationSelectedOptions,
    severitySelectedOptions,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page) => {
    setCurrentPage(page);
    document.getElementById("riskDetectionsTable").scrollIntoView();
  };

  const handleSizePerPageChange = (sizePerPage) => {
    setNumPerPage(sizePerPage);
    setCurrentPage(1);
    document.getElementById("riskDetectionsTable").scrollIntoView();
  };

  const defaultSizePerPageList = [
    {
      text: "10",
      value: 10,
    },
    {
      text: "25",
      value: 25,
    },
    {
      text: "50",
      value: 50,
    },
    {
      text: "100",
      value: 100,
    },
  ];

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    formatRiskDetectionTableData();
    setFilteredTableData(tableData);
  }, [tableData]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatRiskDetectionTableData = async () => {
    setLoading(true);

    let riskDetectionData = [];
    let userData = [];
    let locationData = [];
    let severityData = [];

    // Loop through the list of all risk detections and form the column options
    for (let i = 0; i < tableData?.length; i++) {
      const riskDetectionTableData = tableData[i];
      const userName = `${riskDetectionTableData?.firstName} ${riskDetectionTableData?.lastName}`;

      if (riskDetectionTableData?.riskType) {
        riskDetectionData.push({
          value: riskDetectionTableData.riskType,
          label: riskDetectionTableData.riskType,
        });
      }
      if (userName) {
        userData.push({ value: userName, label: userName });
      }
      if (riskDetectionTableData?.location) {
        locationData.push({
          value: riskDetectionTableData.location,
          label: formatLocationName(riskDetectionTableData.location),
        });
      }
      if (riskDetectionTableData?.severity) {
        severityData.push({
          value: riskDetectionTableData.severity,
          label: riskDetectionTableData.severity,
        });
      }
    }

    const riskDetectionMap = new Map();
    for (const riskObject of riskDetectionData) {
      const currentRiskDetectionCount = riskDetectionMap.get(riskObject.label);

      if (currentRiskDetectionCount !== undefined) {
        riskDetectionMap.set(riskObject.label, currentRiskDetectionCount + 1);
      } else {
        riskDetectionMap.set(riskObject.label, 1);
      }
    }
    for (const riskObject of riskDetectionData) {
      riskObject.count = riskDetectionMap.get(riskObject.label);
    }

    const userMap = new Map();
    for (const userObject of userData) {
      const currentUserCount = userMap.get(userObject.label);

      if (currentUserCount !== undefined) {
        userMap.set(userObject.label, currentUserCount + 1);
      } else {
        userMap.set(userObject.label, 1);
      }
    }
    for (const userObject of userData) {
      userObject.count = userMap.get(userObject.label);
    }

    const locationMap = new Map();
    for (const locationObject of locationData) {
      const currentLocationCount = locationMap.get(locationObject.label);

      if (currentLocationCount !== undefined) {
        locationMap.set(locationObject.label, currentLocationCount + 1);
      } else {
        locationMap.set(locationObject.label, 1);
      }
    }
    for (const locationObject of locationData) {
      locationObject.count = locationMap.get(locationObject.label);
    }

    const severityMap = new Map();
    for (const severityObject of severityData) {
      const currentSeverityCount = severityMap.get(severityObject.label);

      if (currentSeverityCount !== undefined) {
        severityMap.set(severityObject.label, currentSeverityCount + 1);
      } else {
        severityMap.set(severityObject.label, 1);
      }
    }
    for (const severityObject of severityData) {
      severityObject.count = severityMap.get(severityObject.label);
    }

    // Sort by name and remove duplicates
    riskDetectionData = riskDetectionData.filter(
      (v, i, a) => a.findIndex((t) => t.label === v.label) === i
    );
    riskDetectionData.sort((a, b) => a.label?.localeCompare(b.label));
    setRiskDetectionOptions(riskDetectionData);

    userData = userData.filter(
      (v, i, a) => a.findIndex((t) => t.label === v.label) === i
    );
    userData.sort((a, b) => a.label?.localeCompare(b.label));
    setUserOptions(userData);

    locationData = locationData.filter(
      (v, i, a) => a.findIndex((t) => t.label === v.label) === i
    );
    locationData.sort((a, b) => a.label?.localeCompare(b.label));
    setLocationOptions(locationData);

    severityData = severityData.filter(
      (v, i, a) => a.findIndex((t) => t.label === v.label) === i
    );
    severityData.sort((a, b) => a.label?.localeCompare(b.label));
    setSeverityOptions(severityData);

    setLoading(false);
  };

  const filterTable = (dataToFilter) => {
    const filterHelper = (field, filterOptions) => {
      if (filterOptions?.length) {
        dataToFilter = dataToFilter.filter((dataObject) =>
          filterOptions.some((option) => {
            if (field === "user") {
              return (
                `${dataObject.firstName} ${dataObject.lastName}` ===
                option.value
              );
            }

            return option.value === dataObject[`${field}`];
          })
        );
      }
    };

    filterHelper("riskType", riskDetectionSelectedOptions);
    filterHelper("user", userSelectedOptions);
    filterHelper("location", locationSelectedOptions);
    filterHelper("severity", severitySelectedOptions);
    return dataToFilter;
  };

  const updateFilter = (filterType, selectedFilterValueArray) => {
    switch (filterType) {
      case "riskDetection":
        if (selectedFilterValueArray?.length) {
          setRiskDetectionSelectedOptions(selectedFilterValueArray);
        } else {
          setRiskDetectionSelectedOptions([]);
        }
        break;
      case "user":
        if (selectedFilterValueArray?.length) {
          setUserSelectedOptions(selectedFilterValueArray);
        } else {
          setUserSelectedOptions([]);
        }
        break;
      case "location":
        if (selectedFilterValueArray?.length) {
          setLocationSelectedOptions(selectedFilterValueArray);
        } else {
          setLocationSelectedOptions([]);
        }
        break;
      case "severity":
        if (selectedFilterValueArray?.length) {
          setSeveritySelectedOptions(selectedFilterValueArray);
        } else {
          setSeveritySelectedOptions([]);
        }
        break;
      default:
        break;
    }
  };

  function ValueContainer({ children, getValue, ...props }) {
    const { length } = getValue();
    return (
      <components.ValueContainer {...props}>
        {length === 1 && (
          <>
            <span className="multi-select-value">
              {props.selectProps.value[0].label}
            </span>
            <ClearIndicator {...props} />
          </>
        )}
        {length > 1 && (
          <>
            <span className="multi-select-value">{length}</span>
            <ClearIndicator {...props} />
            <components.Placeholder {...props} className="multi-select-label">
              {props.selectProps.placeholder}
            </components.Placeholder>
          </>
        )}
      </components.ValueContainer>
    );
  }

  function ClearIndicator({ innerRef, children, ...props }) {
    const onClear = (e) => {
      if (props.selectProps?.name === "riskDetection") {
        updateFilter("riskDetection", undefined);
      }
      if (props.selectProps?.name === "user") {
        updateFilter("user", undefined);
      }
      if (props.selectProps?.name === "location") {
        updateFilter("location", undefined);
      }
      if (props.selectProps?.name === "severity") {
        updateFilter("severity", undefined);
      }

      e.stopPropagation();
      e.preventDefault();
    };

    return (
      <div ref={innerRef} className="multi-select-clear" onClick={onClear}>
        <components.ClearIndicator {...props}>
          <span className="icon-xs flaticon2-cross p-2" />
        </components.ClearIndicator>
      </div>
    );
  }

  function Option({ children, ...props }) {
    const { isSelected, label, value, isMulti, data } = props;

    const onClick = (e) => {
      props.selectOption({ ...data });
      e.stopPropagation();
      e.preventDefault();
    };

    if (isMulti) {
      return (
        <components.Option {...props}>
          <div className="multi-select-option" onClick={onClick}>
            <Form.Check
              custom
              readOnly
              className="multi-select-check"
              checked={isSelected}
              label={label}
              value={value}
            />
            <span className="multi-select-count">{props.data.count}</span>
          </div>
        </components.Option>
      );
    }
  }

  // Security Check: Click handler for multi-select menus (workaround)
  const handleClick = (target, event) => {
    if (target === "riskDetection") {
      if (showRiskDetectionMenu) {
        setShowRiskDetectionMenu(false);
        if (riskDetectionRef.current.select.inputRef) {
          riskDetectionRef.current.select.inputRef.blur();
        }
      } else {
        setShowRiskDetectionMenu(true);
      }
    }
    if (target === "user") {
      if (showUserMenu) {
        setShowUserMenu(false);
        if (userRef.current.select.inputRef) {
          userRef.current.select.inputRef.blur();
        }
      } else {
        setShowUserMenu(true);
      }
    }
    if (target === "location") {
      if (showLocationMenu) {
        setShowLocationMenu(false);
        if (locationRef.current.select.inputRef) {
          locationRef.current.select.inputRef.blur();
        }
      } else {
        setShowLocationMenu(true);
      }
    }
    if (target === "severity") {
      if (showSeverityMenu) {
        setShowSeverityMenu(false);
        if (severityRef.current.select.inputRef) {
          severityRef.current.select.inputRef.blur();
        }
      } else {
        setShowSeverityMenu(true);
      }
    }
  };

  // Outside click handler for hiding multi-select menus (workaround due to closeMenuOnSelect being disabled)
  const handleClickOutside = (event) => {
    if (
      riskDetectionRef.current &&
      !riskDetectionRef.current.select.controlRef.contains(event.target) &&
      !riskDetectionRef.current.select?.focusedOptionRef?.contains(event.target)
    ) {
      setShowRiskDetectionMenu(false);
      if (riskDetectionRef.current.select.inputRef) {
        riskDetectionRef.current.select.inputRef.blur();
      }
    }
    if (
      userRef.current &&
      !userRef.current.select.controlRef.contains(event.target) &&
      !userRef.current.select?.focusedOptionRef?.contains(event.target)
    ) {
      setShowUserMenu(false);
      if (userRef.current.select.inputRef) {
        userRef.current.select.inputRef.blur();
      }
    }
    if (
      locationRef.current &&
      !locationRef.current.select.controlRef.contains(event.target) &&
      !locationRef.current.select?.focusedOptionRef?.contains(event.target)
    ) {
      setShowLocationMenu(false);
      if (locationRef.current.select.inputRef) {
        locationRef.current.select.inputRef.blur();
      }
    }
    if (
      severityRef.current &&
      !severityRef.current.select.controlRef.contains(event.target) &&
      !severityRef.current.select?.focusedOptionRef?.contains(event.target)
    ) {
      setShowSeverityMenu(false);
      if (severityRef.current.select.inputRef) {
        severityRef.current.select.inputRef.blur();
      }
    }
  };

  const formatRiskDetectionColumn = (cell, row) => {
    const message = ALERT_TYPES[row.riskType]?.message ?? row.riskType;
    return <span>{message}</span>;
  };
  const formatUserColumn = (cell, row) => (
    <span>{`${row.firstName} ${row.lastName}`}</span>
  );
  const formatLocationColumn = (cell, row) => (
    <span>{formatLocationName(row.location)}</span>
  );
  const formatSeverityColumn = (cell, row) => (
    <>
      <span style={{ float: "left" }} className="severity-icon">
        <i
          className={`fa fa-circle font-sm ${clsx({
            "font-color-high-risk": row.severity === "high",
            "font-color-med-risk": row.severity === "medium",
            "font-color-low-risk": row.severity === "low",
            "font-color-informational-risk": row.severity === "informational",
          })}`}
        />
      </span>
      &nbsp;
      <span style={{ padding: "0 5px" }} className="severity-label">
        {language[`RISKS.${row.severity}`] || "Unknown"}
      </span>
    </>
  );
  const formatDateColumn = (cell, row) => <span>{row.date}</span>;

  const formatPDFTableHeader = (text) => (
    <span className="tableHeaderPdf">{text}</span>
  );

  const formatRiskDetectionHeader = () => {
    if (componentIsForPdf) {
      return formatPDFTableHeader("Risk Detection");
    }

    return (
      <div
        className="select-container"
        onClick={(e) => handleClick("riskDetection", e)}
      >
        <Select
          id="riskDetection"
          ref={riskDetectionRef}
          autoBlur
          name="riskDetection"
          classNamePrefix="multi-select"
          styles={multiSelectStyle}
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          menuIsOpen={showRiskDetectionMenu}
          options={riskDetectionOptions}
          value={riskDetectionSelectedOptions}
          onChange={(value) => updateFilter("riskDetection", value)}
          placeholder="Risk Detection"
          isClearable={false}
          isSearchable
          components={
            riskDetectionSelectedOptions?.length
              ? { MenuList: ScrollMenu, ClearIndicator, Option, ValueContainer }
              : { MenuList: ScrollMenu, Option }
          }
        />
      </div>
    );
  };

  const formatUserHeader = () => {
    const text = "User";

    if (componentIsForPdf) {
      return formatPDFTableHeader(text);
    }

    return (
      <div className="select-container" onClick={(e) => handleClick("user", e)}>
        <Select
          id="user"
          ref={userRef}
          autoBlur
          name="user"
          classNamePrefix="multi-select"
          styles={multiSelectStyle}
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          menuIsOpen={showUserMenu}
          options={userOptions}
          value={userSelectedOptions}
          onChange={(value) => updateFilter("user", value)}
          placeholder={text}
          isClearable={false}
          isSearchable
          components={
            userSelectedOptions?.length
              ? { MenuList: ScrollMenu, ClearIndicator, Option, ValueContainer }
              : { MenuList: ScrollMenu, Option }
          }
        />
      </div>
    );
  };

  const formatLocationHeader = () => {
    const text = "Location";

    if (componentIsForPdf) {
      return formatPDFTableHeader(text);
    }

    return (
      <div
        className="select-container"
        onClick={(e) => handleClick("location", e)}
      >
        <Select
          id="location"
          ref={locationRef}
          autoBlur
          name="location"
          classNamePrefix="multi-select"
          styles={multiSelectStyle}
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          menuIsOpen={showLocationMenu}
          options={locationOptions}
          value={locationSelectedOptions}
          onChange={(value) => updateFilter("location", value)}
          placeholder={text}
          isClearable={false}
          isSearchable
          components={
            locationSelectedOptions?.length
              ? { MenuList: ScrollMenu, ClearIndicator, Option, ValueContainer }
              : { MenuList: ScrollMenu, Option }
          }
        />
      </div>
    );
  };

  const formatSeverityHeader = () => {
    const text = "Severity";

    if (componentIsForPdf) {
      return formatPDFTableHeader(text);
    }

    return (
      <div
        className="select-container"
        onClick={(e) => handleClick("severity", e)}
      >
        <Select
          id="severity"
          ref={severityRef}
          autoBlur
          name="severity"
          classNamePrefix="multi-select"
          styles={multiSelectStyle}
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          menuIsOpen={showSeverityMenu}
          options={severityOptions}
          value={severitySelectedOptions}
          onChange={(value) => updateFilter("severity", value)}
          placeholder="Severity"
          isClearable={false}
          isSearchable
          components={
            severitySelectedOptions?.length
              ? { MenuList: ScrollMenu, ClearIndicator, Option, ValueContainer }
              : { MenuList: ScrollMenu, Option }
          }
        />
      </div>
    );
  };

  const columns = [
    {
      dataField: "id",
      hidden: true,
    },
    {
      dataField: "riskType",
      text: "Risk Detection",
      formatter: formatRiskDetectionColumn,
      headerFormatter: formatRiskDetectionHeader,
      headerClasses: componentIsForPdf ? "" : "selectable",
      sort: false,
      title: true,
    },
    {
      dataField: "user",
      text: "User",
      formatter: formatUserColumn,
      headerFormatter: formatUserHeader,
      headerClasses: componentIsForPdf ? "" : "selectable",
      sort: false,
      title: true,
    },
    {
      dataField: "location",
      text: "Location",
      formatter: formatLocationColumn,
      headerFormatter: formatLocationHeader,
      headerClasses: componentIsForPdf ? "" : "selectable",
      sort: false,
      title: true,
    },
    {
      dataField: "severity",
      text: "Severity",
      formatter: formatSeverityColumn,
      headerFormatter: formatSeverityHeader,
      headerClasses: componentIsForPdf ? "" : "selectable",
      sort: false,
      title: true,
    },
    {
      dataField: "date",
      text: "Date",
      formatter: formatDateColumn,
      sort: true,
      headerClasses: componentIsForPdf ? "tableHeaderPdf" : "",
      onSort: componentIsForPdf
        ? null
        : () => setTableSort(tableSort === "desc" ? "asc" : "desc"),
      sortCaret: componentIsForPdf
        ? null
        : (order, column) => sortingCarets(order, column),
      title: true,
    },
  ];

  return (
    <div id="riskDetectionsTable">
      <Row>
        <Col sm="12" md="12" lg="12" xl="12">
          {isLoading || isParentLoading ? (
            <Portlet
              fluidHeight={false}
              className="portlet--border-bottom-dark"
            >
              <PortletBody fluid style={{ padding: 0 }}>
                <>
                  <Spinner />
                </>
              </PortletBody>
            </Portlet>
          ) : showPagination ? (
            <PaginationProvider
              pagination={paginationFactory({
                custom: true,
                page: currentPage,
                totalSize: filteredTableData?.length,
                sizePerPage: numPerPage,
                onPageChange: onPageChange || handlePageChange,
                onSizePerPageChange:
                  onSizePerPageChange || handleSizePerPageChange,
                sizePerPageList: sizePerPageList || defaultSizePerPageList,
              })}
            >
              {({ paginationProps, paginationTableProps }) => (
                <ToolkitProvider
                  keyField="id"
                  data={filteredTableData}
                  columns={columns}
                >
                  {(props) => (
                    <Row>
                      <Col md="12">
                        <BootstrapTable
                          {...props.baseProps}
                          keyField={keyField}
                          loading={isLoading}
                          filter={filterFactory()}
                          bordered={false}
                          hover={!!(!isLoading && filteredTableData?.length)}
                          bootstrap4
                          sort={{
                            dataField: "date",
                            order: tableSort,
                          }}
                          noDataIndication={
                            isError ? (
                              <GeneralError />
                            ) : isLoading ? (
                              <Spinner />
                            ) : (
                              <div className="no-data">
                                {language["GENERAL.NO_DATA"]}
                              </div>
                            )
                          }
                          {...paginationTableProps}
                        />
                        {filteredTableData?.length > 10 && (
                          <div className="pagination">
                            <div>
                              <ButtonGroup>
                                {paginationProps.sizePerPageList.map(
                                  (option) => (
                                    <Button
                                      key={option.text}
                                      onClick={() =>
                                        handleSizePerPageChange(option.value)
                                      }
                                      variant={
                                        numPerPage === option.value
                                          ? "primary"
                                          : "light"
                                      }
                                    >
                                      {option.text}
                                    </Button>
                                  )
                                )}
                              </ButtonGroup>
                            </div>
                            <div>
                              <PaginationListStandalone {...paginationProps} />
                            </div>
                          </div>
                        )}
                      </Col>
                    </Row>
                  )}
                </ToolkitProvider>
              )}
            </PaginationProvider>
          ) : (
            <ToolkitProvider
              keyField="id"
              data={filteredTableData}
              columns={columns}
            >
              {(props) => (
                <Row>
                  <Col md="12">
                    <BootstrapTable
                      {...props.baseProps}
                      keyField={keyField}
                      loading={isLoading}
                      filter={filterFactory()}
                      bordered={false}
                      hover={!!(!isLoading && filteredTableData?.length)}
                      bootstrap4
                      sort={{
                        dataField: "date",
                        order: tableSort,
                      }}
                      noDataIndication={
                        isError ? (
                          <GeneralError />
                        ) : isLoading ? (
                          <Spinner />
                        ) : (
                          <div className="no-data">
                            {language["GENERAL.NO_DATA"]}
                          </div>
                        )
                      }
                    />
                  </Col>
                </Row>
              )}
            </ToolkitProvider>
          )}
        </Col>
      </Row>
    </div>
  );
}
